import {Platform} from 'react-native';
import {reaction} from 'mobx';

import Config from '../config.generated';
import type {RootStore} from '../stores/rootStore';
import dev from '../utils/dev';

import {sanitizeCrashlyticsAttributes} from './crashlyticsSanitize';

class CrashlyticsService {
  private initialized = false;
  private disposers: (() => void)[] = [];

  /**
   * Initialize Crashlytics - call once at app startup
   * Only enables in production mobile builds
   */
  async initialize(rootStore: RootStore): Promise<void> {
    // Skip on web or if already initialized
    if (Platform.OS === 'web' || this.initialized) {
      return;
    }

    // Only enable in production
    const isProduction = !dev.enableDebugging();

    // Dynamic import to avoid web bundle issues
    const crashlytics = (await import('@react-native-firebase/crashlytics')).default;
    await crashlytics().setCrashlyticsCollectionEnabled(isProduction);

    if (!isProduction) {
      return; // Don't set up user tracking in dev
    }

    this.initialized = true;

    // Set up tracking (implemented in next tasks)
    await this.setupBrandAttribute();
    this.setupUserTracking(rootStore);
    this.setupContextTracking(rootStore);
  }

  /**
   * Sets a single Crashlytics attribute only if it is not a PII key (DIS-1167).
   * All attribute writes route through this chokepoint so personal data can
   * never reach Crashlytics, even if a future caller passes it.
   */
  private async setAttributeSafe(key: string, value: string): Promise<void> {
    if (Object.keys(sanitizeCrashlyticsAttributes({[key]: value})).length === 0) {
      return;
    }
    const crashlytics = (await import('@react-native-firebase/crashlytics')).default;
    await crashlytics().setAttribute(key, value);
  }

  private async setupBrandAttribute(): Promise<void> {
    await this.setAttributeSafe('brand', Config.BRAND_ID || 'unknown');
  }

  private setupUserTracking(rootStore: RootStore): void {
    const {userStore} = rootStore;

    // Update Crashlytics when user logs in/out. Only the opaque MFL user id is
    // sent — never raw PII (email/name/phone/zohoId). See DIS-1167 and
    // sanitizeCrashlyticsAttributes.
    this.disposers.push(
      reaction(
        () => ({
          isLoggedIn: userStore.isLoggedIn,
          userId: userStore.id,
        }),
        async ({isLoggedIn, userId}) => {
          const crashlytics = (await import('@react-native-firebase/crashlytics')).default;
          await crashlytics().setUserId(isLoggedIn && userId ? userId : '');
        },
        {fireImmediately: true},
      ),
    );
  }

  private setupContextTracking(rootStore: RootStore): void {
    const {shopSelectionStore} = rootStore;

    // Track selected store
    this.disposers.push(
      reaction(
        () => shopSelectionStore.selectedStoreSummary?.id,
        async storeId => {
          await this.setAttributeSafe('storeId', storeId || '');
        },
        {fireImmediately: true},
      ),
    );
  }

  /** Record a non-fatal error */
  recordError(error: Error, context?: string): void {
    if (!this.initialized) return;

    // Fire and forget - don't await in error path
    (async () => {
      const crashlytics = (await import('@react-native-firebase/crashlytics')).default;
      if (context) {
        crashlytics().log(`Context: ${context}`);
      }
      crashlytics().recordError(error);
    })();
  }

  /** Log a message (breadcrumb) */
  log(message: string): void {
    if (!this.initialized) return;

    (async () => {
      const crashlytics = (await import('@react-native-firebase/crashlytics')).default;
      crashlytics().log(message);
    })();
  }

  /** Set current screen for context */
  setCurrentScreen(screenName: string): void {
    if (!this.initialized) return;

    (async () => {
      await this.setAttributeSafe('currentScreen', screenName);
    })();
  }
}

export const crashlyticsService = new CrashlyticsService();
